<template>
  <!-- The read-only hazard details opened from a "warnings on route" row: the
       same panel a map marker opens (HazardDetailSheet), for pages and sheets
       that don't host the marker layer. A modal on the hike page; a panel
       beside the map in the planner. Works signed out — hazard(id:) is public. -->
  <Teleport to="body">
    <HazardDetailSheet
      :open="isOpen"
      :loading="loading"
      :failed="failed"
      :details="details"
      :panel-style="panelStyle"
      :modal="modal"
      :pager="pager"
      :refreshing="refreshing"
      @close="close"
      @photo-error="onPhotoError"
      @pager-previous="$emit('pager-previous')"
      @pager-next="$emit('pager-next')"
    />
    <div class="position-fixed top-0 end-0 p-3 hazard-toast-wrap">
      <div ref="toastEl" class="toast hazard-toast" role="status" aria-live="polite" aria-atomic="true">
        <div class="toast-body" data-test="hazard-details-toast">{{ toastMessage }}</div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import { Toast } from 'bootstrap';
import HazardDetailSheet from './components/HazardDetailSheet.vue';
import graph_client from '../../utils/graphql/graph_client';
import { reportHandledError } from '../../utils/bugsnag';
import { HazardLoader } from '../../utils/trailHazards/hazardLoader';
import { buildHazardDetails } from '../../utils/trailHazards/hazardDetails';
import type { Hazard, HazardDetailsView } from '../../utils/trailHazards/hazardDetails';
import { hazardPanelStyle } from '../../utils/trailHazards/hazardPanelPlacement';
import { hazardViewedEvent, trackHazardEvent } from '../../utils/trailHazards/hazardAnalytics';
import type { PagerView } from '../../utils/routeAlerts/routeAlertGroups';

export default defineComponent({
  name: 'HazardDetailsModal',
  components: { HazardDetailSheet },
  props: {
    /** The `screen` its hazard_viewed events carry (hike_overview, planner). */
    screen: { type: String, required: true },
    /** A modal (backdrop, centred) — for a page with no map behind it. */
    modal: { type: Boolean, default: false },
    /**
     * Pin the panel to the map's top-right like the POI sheet (the planner, the
     * hike map). The map follows the hazard shown, so on a phone the bottom
     * sheet is capped to leave it in view above.
     */
    placeToMap: { type: Boolean, default: false },
    /**
     * The pager through the card's group (‹ 1 / 3 ›). The host owns it: on an
     * arrow it moves on and calls open(nextId, { keepDetails: true }).
     */
    pager: { type: Object as PropType<PagerView | null>, default: null },
  },
  emits: ['close', 'gone', 'pager-previous', 'pager-next'],
  data() {
    return {
      isOpen: false,
      loading: false,
      /** Stepping: the shown details stay (faded) until the next hazard has loaded. */
      refreshing: false,
      failed: false,
      details: null as HazardDetailsView | null,
      panelStyle: null as Record<string, string> | null,
      toastMessage: '',
      currentId: null as string | null,
      photoRefetched: false,
    };
  },
  created() {
    (this as any).loader = new HazardLoader(graph_client as any, reportHandledError);
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.position);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.position);
    (this as any).loader.cancel();
  },
  methods: {
    /**
     * Opens the panel on a hazard: a short loading state, then its details.
     * `keepDetails` (a pager step) leaves the shown hazard up, faded, until the
     * next one arrives, so the panel doesn't collapse and regrow on every step.
     */
    async open(id: string, { keepDetails = false }: { keepDetails?: boolean } = {}) {
      const stepping = keepDetails && this.isOpen && !!this.details;
      // A modal takes focus (its close button) and gives it back on close.
      const takeFocus = this.modal && !this.isOpen;
      if (takeFocus) (this as any).returnFocusTo = document.activeElement as HTMLElement | null;
      this.currentId = id;
      this.isOpen = true;
      if (takeFocus) {
        // After the render the open state queues, so the button exists.
        this.$nextTick(() => {
          (document.querySelector('[data-hazard-sheet] [data-test="hazard-close"]') as HTMLElement | null)?.focus();
        });
      }
      this.failed = false;
      if (stepping) {
        this.refreshing = true;
      } else {
        this.loading = true;
        this.details = null;
      }
      this.photoRefetched = false;
      this.$nextTick(this.position);

      const result = await (this as any).loader.load(id);
      if (result.kind === 'stale') return;
      this.refreshing = false;
      if (result.kind === 'gone') { this.leftTheMap(); return; }
      this.loading = false;
      if (result.kind === 'failed') { this.failed = true; this.details = null; return; }

      this.show(result.hazard);
      trackHazardEvent(hazardViewedEvent(result.hazard, this.screen));
    },

    show(hazard: Hazard) {
      this.details = buildHazardDetails(hazard, new Date(), (key, values) => this.$t(key, values ?? {}) as string);
    },

    /** Null or no longer active: close, say so, and let the list refresh. */
    leftTheMap() {
      const id = this.currentId;
      this.close();
      this.showToast(this.$t('trail_hazards.no_longer_active') as string);
      this.$emit('gone', id);
    },

    async onPhotoError() {
      if (this.photoRefetched || !this.currentId || !this.isOpen) return;
      this.photoRefetched = true;
      const result = await (this as any).loader.load(this.currentId);
      if (result.kind === 'shown') this.show(result.hazard);
      else if (result.kind === 'gone') this.leftTheMap();
    },

    close() {
      const wasOpen = this.isOpen;
      this.isOpen = false;
      this.loading = false;
      this.refreshing = false;
      this.failed = false;
      this.details = null;
      this.currentId = null;
      (this as any).loader.cancel();
      if (wasOpen && this.modal) {
        const target = (this as any).returnFocusTo as HTMLElement | null;
        (this as any).returnFocusTo = null;
        if (target && document.contains(target)) target.focus?.();
      }
      if (wasOpen) this.$emit('close');
    },

    showToast(message: string) {
      this.toastMessage = message;
      const el = this.$refs.toastEl as HTMLElement | undefined;
      if (!el) return;
      try {
        Toast.getOrCreateInstance(el, { autohide: true, delay: 5000 }).show();
      } catch (error) {
        console.warn('[route_alerts] toast failed', error);
      }
    },

    position() {
      if (!this.isOpen || !this.placeToMap) return;
      const map = document.querySelector('.mapboxgl-map');
      const dock = document.querySelector('[data-map-dock="right"]');
      this.panelStyle = hazardPanelStyle({
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        mapRect: map ? map.getBoundingClientRect() : null,
        dockLeft: dock ? dock.getBoundingClientRect().left : null,
        followsMap: true,
      });
    },

    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.isOpen) this.close();
    },
  },
});
</script>

<style scoped>
.hazard-toast-wrap {
  z-index: 9999;
  pointer-events: none;
}

.hazard-toast {
  pointer-events: auto;
}

.hazard-toast .toast-body {
  color: #000;
  background-color: #fff;
  border-radius: inherit;
}
</style>
